import { useTerminology } from '../../../terminology/TerminologyContext';
// ResellerDashboard.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams, Outlet } from 'react-router-dom';
import { getUsers } from '../../services/api.js';
import Clients from '../pages/Clients.js';
import EnterpriseClients from '../pages/EnterpriseClients.js';
import Account from '../pages/Account';
import Reports from '../pages/Reports';
import ThemeContext from '../../ThemeContext';
import { URL_SEGMENTS, FULL_ROUTES } from '../../../config/Routesconfig';
import ResellerConfig from '../../../config/ResellerConfig';

// ───────────────
// Route alias
// ───────────────
const resellerRoutes = FULL_ROUTES.resellerRoutes;

// ───────────────
// Error Boundary
// ───────────────
class ErrorBoundary extends React.Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      const theme = this.context?.theme || {
        colors: { errorBorder: '#f5c2c7', errorText: '#842029', errorBg: '#f8d7da' },
        typography: { fontFamily: 'Arial, sans-serif', bodyFontSize: 14 },
      };
      return (
        <div style={{
          padding: '0.75rem 1.25rem', marginBottom: '1rem',
          border: `1px solid ${theme.colors.errorBorder}`, borderRadius: '0.25rem',
          color: theme.colors.errorText, backgroundColor: theme.colors.errorBg,
          textAlign: 'center', width: '100%', maxWidth: 'min(90vw, 1100px)',
          boxSizing: 'border-box',
          fontFamily: theme.typography?.fontFamily || 'Arial, sans-serif',
          fontSize: `${theme.typography?.bodyFontSize || 14}px`,
        }}>
          Something went wrong: {this.state.error?.message || 'Unknown error'}
        </div>
      );
    }
    return this.props.children;
  }
}
ErrorBoundary.contextType = ThemeContext;

// ───────────────
// ResellerDashboard
// ───────────────
const ResellerDashboard = () => {
  useTerminology();
  const { theme } = React.useContext(ThemeContext);

  // ── Nav config from ResellerConfig ───────
  const nav = ResellerConfig.nav;

  // ── State ────────────────
  const [activeTab,    setActiveTab]    = useState(URL_SEGMENTS.clients);
  const [activeSubTab, setActiveSubTab] = useState(null);
  const [users,        setUsers]        = useState([]);
  const [error,        setError]        = useState('');
  const [currentUser,  setCurrentUser]  = useState(
    JSON.parse(localStorage.getItem('user')) || {}
  );

  const [isReportsDropdownOpen, setIsReportsDropdownOpen] = useState(false);
  const [isReportsPinned,       setIsReportsPinned]       = useState(false);

  const reportsDropdownRef = React.useRef(null);

  const navigate     = useNavigate();
  const location     = useLocation();
  const { username } = useParams();

  const fontFamily   = theme.typography?.fontFamily || '"Roboto", "Helvetica Neue", Arial, sans-serif';
  const bodyFontSize = theme.typography?.bodyFontSize || 14;

  const dropdownItemStyle = (isActive) => ({
    width: '100%', padding: '0.75rem 1rem', textAlign: 'left',
    background: isActive ? theme.gradients.primary : 'transparent',
    color: isActive ? '#fff' : theme.colors.textPrimary,
    border: 'none', cursor: 'pointer',
    fontSize: `${bodyFontSize}px`, fontFamily,
    transition: 'all 0.2s ease',
  });

  // ── Shared tab button style ──
  const tabBtnStyle = (isActive) => ({
    flex: '1 1 0', minWidth: '100px', padding: '0.4rem',
    fontSize: `${bodyFontSize}px`, fontWeight: 'bold',
    borderRadius: '5px', border: 'none',
    background: isActive ? theme.gradients.primary : theme.gradients.secondary,
    color: theme.colors.textWhite, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: '0.5rem', fontFamily, transition: 'opacity 0.3s ease',
    width: '100%',
  });

  // ── Detect active tab from URL ────────────
  useEffect(() => {
    const path         = location.pathname;
    const searchParams = new URLSearchParams(location.search);
    const reportSubKey = searchParams.get('sub');

    if (
      path.includes(`/${URL_SEGMENTS.enterpriseClients}`) ||
      path.includes(`/${URL_SEGMENTS.addEnterpriseClient}`) ||
      path.includes(`/${URL_SEGMENTS.editEnterpriseClient}/`)
    ) {
      setActiveTab(URL_SEGMENTS.enterpriseClients);
      setActiveSubTab(null);

    } else if (
      path.includes(`/${URL_SEGMENTS.clients}`) ||
      path.includes(`/${URL_SEGMENTS.addClient}`) ||
      path.includes(`/${URL_SEGMENTS.editClient}/`)
    ) {
      setActiveTab(URL_SEGMENTS.clients);
      setActiveSubTab(null);

    } else if (path.includes(`/${URL_SEGMENTS.reports}`)) {
      setActiveTab(URL_SEGMENTS.reports);
      setActiveSubTab(
        ['user', 'commission', 'usage'].includes(reportSubKey)
          ? reportSubKey
          : 'user'
      );

    } else if (path.includes(`/${URL_SEGMENTS.account}`)) {
      setActiveTab(URL_SEGMENTS.account);
      setActiveSubTab(null);

    } else {
      setActiveTab(URL_SEGMENTS.clients);
      setActiveSubTab(null);
    }

    // Fetch users
    const fetchUsers = async () => {
      try {
        const response     = await getUsers();
        const fetchedUsers = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data.users)
          ? response.data.users
          : [];
        setUsers(fetchedUsers.filter((u) => u.role === 'client'));
        setError(fetchedUsers.length === 0 ? `No ${ResellerConfig.clients.plural.toLowerCase()} found.` : '');
      } catch (err) {
        console.error('Fetch users error:', err.response?.data, err.response?.status);
        if (err.response?.status === 401) {
          setError('Unauthorized access. Please log in again.');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          navigate(FULL_ROUTES.login);
        } else {
          setError(`Failed to fetch ${ResellerConfig.clients.plural.toLowerCase()}. Please try again.`);
          setUsers([]);
        }
      }
    };
    fetchUsers();
  }, [navigate, location, username]);

  // ── Close dropdown on outside click ──────
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (reportsDropdownRef.current && !reportsDropdownRef.current.contains(e.target)) {
        setIsReportsDropdownOpen(false);
        setIsReportsPinned(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── Handlers ─────────────
  const handleTabClick = (tab) => {
    if (tab === URL_SEGMENTS.reports) {
      const next = !isReportsPinned;
      setIsReportsPinned(next);
      setIsReportsDropdownOpen(next);
      return;
    }
    setActiveTab(tab);
    setError('');
    setIsReportsDropdownOpen(false);
    setIsReportsPinned(false);
    navigate(`/${username}/${tab}`);
  };

  const handleReportSubTab = (subTab) => {
    setActiveTab(URL_SEGMENTS.reports);
    setActiveSubTab(subTab);
    setError('');
    navigate(`/${username}/${URL_SEGMENTS.reports}?sub=${subTab}`);
    setIsReportsDropdownOpen(false);
    setIsReportsPinned(false);
  };

  // ── Render content ───────
  const renderContent = () => {
    const path = location.pathname;

    // Nested form routes → Outlet
    if (
      path.includes(`/${URL_SEGMENTS.addClient}`)           ||
      path.includes(`/${URL_SEGMENTS.editClient}/`)         ||
      path.includes(`/${URL_SEGMENTS.addEnterpriseClient}`) ||
      path.includes(`/${URL_SEGMENTS.editEnterpriseClient}/`)
    ) {
      return <Outlet />;
    }

    switch (activeTab) {
      case URL_SEGMENTS.clients:
        return <Clients users={users} />;
      case URL_SEGMENTS.enterpriseClients:
        return <EnterpriseClients />;
      case URL_SEGMENTS.account:
        return <Account currentUser={currentUser} setCurrentUser={setCurrentUser} setError={setError} />;
      case URL_SEGMENTS.reports: {
        const effectiveSub = activeSubTab || 'user';
        return <Reports activeSubTab={effectiveSub} />;
      }
      default:
        return <div>No content available for this tab.</div>;
    }
  };

  // ── Tab active check helper ──
  // Clients tab covers both regular and enterprise clients (no separate nav tab)
  const isClientsTabActive = () =>
    activeTab === URL_SEGMENTS.clients ||
    activeTab === URL_SEGMENTS.enterpriseClients ||
    location.pathname.includes(`/${URL_SEGMENTS.addClient}`) ||
    location.pathname.includes(`/${URL_SEGMENTS.editClient}/`) ||
    location.pathname.includes(`/${URL_SEGMENTS.addEnterpriseClient}`) ||
    location.pathname.includes(`/${URL_SEGMENTS.editEnterpriseClient}/`);

  // ── Report dropdown items from ResellerConfig ────────────────────────────
  const reportItems = [
    { key: 'user',       label: ResellerConfig.reports.items.user       },
    { key: 'commission', label: ResellerConfig.reports.items.commission  },
    { key: 'usage',      label: ResellerConfig.reports.items.usage       },
  ];

  // ── Render ───────────────
  return (
    <ErrorBoundary>
      <div style={{ fontFamily, fontSize: `${bodyFontSize}px`, width: '100%', boxSizing: 'border-box', textAlign: 'initial' }}>

        {/* ── Navigation tabs ── */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>

          {/* ── Clients tab (covers enterprise clients too) ── */}
          <button
            style={tabBtnStyle(isClientsTabActive())}
            onClick={() => handleTabClick(URL_SEGMENTS.clients)}
            onMouseOver={(e) => (e.currentTarget.style.opacity = '0.9')}
            onMouseOut={(e)  => (e.currentTarget.style.opacity = '1')}
          >
            <i className={`bi bi-${nav.clients.icon}`} style={{ fontSize: `${Math.max(bodyFontSize - 2, 10)}px` }} />
            {nav.clients.label}
          </button>

          {/* ── Reports tab with dropdown ── */}
          <div
            style={{ flex: '1 1 0', minWidth: '100px', position: 'relative' }}
            ref={reportsDropdownRef}
            onMouseEnter={() => setIsReportsDropdownOpen(true)}
            onMouseLeave={() => { if (!isReportsPinned) setIsReportsDropdownOpen(false); }}
          >
            <button
              style={tabBtnStyle(activeTab === URL_SEGMENTS.reports)}
              onClick={() => handleTabClick(URL_SEGMENTS.reports)}
              onMouseOver={(e) => (e.currentTarget.style.opacity = '0.9')}
              onMouseOut={(e)  => (e.currentTarget.style.opacity = '1')}
            >
              <i className={`bi bi-${nav.reports.icon}`} style={{ fontSize: `${Math.max(bodyFontSize - 2, 10)}px` }} />
              <span>{nav.reports.label}</span>
              <i className="bi bi-chevron-down" style={{ fontSize: '12px' }} />
            </button>

            {isReportsDropdownOpen && (
              <div style={{
                position: 'absolute', top: '100%', left: 0,
                backgroundColor: theme.colors.bgPrimary,
                border: `1px solid ${theme.colors.borderColor}`,
                borderRadius: '6px', boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
                zIndex: 30, width: '100%', minWidth: '180px', overflow: 'hidden',
              }}>
                {reportItems.map((item, idx, arr) => (
                  <React.Fragment key={item.key}>
                    <button
                      style={dropdownItemStyle(activeSubTab === item.key)}
                      onClick={() => handleReportSubTab(item.key)}
                      onMouseOver={(e) => activeSubTab !== item.key && (e.currentTarget.style.background = theme.gradients.secondary)}
                      onMouseOut={(e)  => activeSubTab !== item.key && (e.currentTarget.style.background = 'transparent')}
                    >
                      {item.label}
                    </button>
                    {idx < arr.length - 1 && (
                      <hr style={{ margin: 0, border: 'none', borderTop: `1px solid ${theme.colors.borderColor}`, opacity: 0.5 }} />
                    )}
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>

          {/* ── Account tab ── */}
          <button
            style={tabBtnStyle(activeTab === URL_SEGMENTS.account)}
            onClick={() => handleTabClick(URL_SEGMENTS.account)}
            onMouseOver={(e) => (e.currentTarget.style.opacity = '0.9')}
            onMouseOut={(e)  => (e.currentTarget.style.opacity = '1')}
          >
            <i className={`bi bi-${nav.account.icon}`} style={{ fontSize: `${Math.max(bodyFontSize - 2, 10)}px` }} />
            {nav.account.label}
          </button>

        </div>

        {/* ── Error banner ── */}
        {error && (
          <div style={{
            padding: '0.75rem 1.25rem', marginBottom: '1rem',
            border: `1px solid ${theme.colors.errorBorder}`, borderRadius: '0.25rem',
            color: theme.colors.errorText, backgroundColor: theme.colors.errorBg,
            textAlign: 'center', width: '100%', boxSizing: 'border-box',
            fontFamily, fontSize: `${bodyFontSize}px`,
          }}>
            {error}
          </div>
        )}

        {/* ── Page content ── */}
        <div style={{ width: '100%', boxSizing: 'border-box' }}>
          {renderContent()}
        </div>

      </div>
    </ErrorBoundary>
  );
};

export default ResellerDashboard;